import axios from 'axios';
import { ApolloClient } from 'apollo-client';
import { ApolloLink, split } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import uuid from 'uuid/v4';
import { logout, getAuthToken, getAuthHeader } from './auth';
import router from '../router';
import { IgnoreError } from './errors';

const sessionId = uuid();

function handleUnauthorized(message) {
  logout();
  router.push({
    name: 'login',
    params: { message },
  });
}

// TODO
// https://www.apollographql.com/docs/react/recipes/fragment-matching.html
// Make this part of a build step - can probably get info from makeExecutableSchema
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: {
    __schema: {
      types: [
        {
          kind: 'INTERFACE',
          name: 'FoodItem',
          possibleTypes: [
            { name: 'Recipe' },
            { name: 'Restaurant' },
          ],
        },
      ],
    },
  },
});

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (networkError && networkError.statusCode === 401) {
    handleUnauthorized(networkError.result);
  }

  if (graphQLErrors) {
    graphQLErrors.map(({ message, location, path }) =>
      /* eslint-disable no-console */
      console.error(`Graphql error: ${message}, ${location}, ${path}`));
  }
});

const httpLink = new HttpLink({
  uri: `${window.location.protocol}//${window.location.host}/api/graphql/exec`,
});

const wsProtocol = /^https/.test(window.location.protocol) ? 'wss' : 'ws';
const wsLink = new WebSocketLink({
  uri: `${wsProtocol}://${window.location.host}/api/graphql/subscriptions`,
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: {
      authToken: getAuthToken(),
      sessionId,
    },
  },
});

const authLink = setContext((_, { headers }) => {
  const authHeader = getAuthHeader();
  const sessionIdHeader = {
    'X-Session-Id': sessionId,
  };

  return {
    headers: {
      ...headers,
      ...authHeader,
      ...sessionIdHeader,
    },
  };
});

const fullHttpLink = ApolloLink.from([authLink, errorLink, httpLink]);

const wsAuthLink = new ApolloLink((operation, forward) => {
  const authHeader = getAuthHeader();
  const sessionIdHeader = {
    'X-Session-Id': sessionId,
  };
  operation.setContext({
    headers: {
      ...authHeader,
      ...sessionIdHeader,
    },
  });

  operation.authToken = getAuthToken();
  operation.sessionId = sessionId;
  return forward(operation);
});

const fullWsLink = ApolloLink.from([wsAuthLink, wsLink]);

const link = split(({ query }) => {
  const { kind, operation } = getMainDefinition(query);
  return kind === 'OperationDefinition' && operation === 'subscription';
}, fullWsLink, fullHttpLink);

const graphql = new ApolloClient({
  link,
  cache: new InMemoryCache({ fragmentMatcher }),
  connectToDevTools: true,
});

graphql.localExtensions = {
  invalidateStoreQuery(queryName) {
    // TODO this is not documented API
    const rootQuery = graphql.store.cache.data.data.ROOT_QUERY;
    Object.keys(rootQuery).filter(query => query.indexOf(queryName) === 0)
      .forEach((query) => {
        delete rootQuery[query];
      });
  },
};

axios.interceptors.response.use(response => response,
(error) => {
  if (error.response.status === 401 && router.currentRoute.name !== 'login') {
    handleUnauthorized(error.response.data);
    return Promise.reject(new IgnoreError('Interceptor handled 401'));
  }

  return Promise.reject(error);
});

const http = axios;

export {
  graphql,
  http,
};



// WEBPACK FOOTER //
// ./src/utils/fetch.js